package com.dotdrives.controller

import com.dotdrives.core.DotController
import com.dotdrives.model.AuthAudience
import com.dotdrives.model.AuthFormState
import com.dotdrives.model.AuthProvider
import com.dotdrives.model.AuthSessionSnapshot
import com.dotdrives.model.AuthSessionSource
import com.dotdrives.model.OrgSummary
import com.dotdrives.model.Profile
import com.dotdrives.model.SessionInfo
import com.dotdrives.model.SignUpOutcome
import com.dotdrives.repository.AuthRepository
import com.dotdrives.repository.OrgRepository
import com.dotdrives.repository.ProfileRepository
import io.github.jan.supabase.auth.exception.AuthRestException
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.launch
import kotlin.time.Clock

data class AuthUiState(
    val checked: Boolean = false,
    val staffRole: String? = null,
    val staffEntryChecked: Boolean = false,
    val staffSignInRequired: Boolean = false,
    val session: SessionInfo? = null,
    val profileDisplayName: String? = null,
    val orgs: List<OrgSummary> = emptyList(),
    val activeOrgId: String? = null,
    val resolvedDeepLinkOrgId: String? = null,
    val deepLinkAttemptOrgId: String? = null,
    val needsOnboarding: Boolean = false,
    val loading: Boolean = false,
    val error: String? = null,
    val bootstrapError: String? = null,
    val confirmationRequired: Boolean = false,
    val magicLinkSent: Boolean = false,
    val passwordResetSent: Boolean = false,
    val passwordUpdated: Boolean = false,
    val recoveryPending: Boolean = false,
    val audience: AuthAudience = AuthAudience.Team,
    val formState: AuthFormState = AuthFormState.SignIn,
    val giverContext: Boolean = false,
    val pendingEmail: String = "",
    val resendAvailableAt: Long? = null,
    val resendSecondsRemaining: Int = 0,
    val credentialError: Boolean = false,
) {
    val emailCooldownActive: Boolean
        get() = resendSecondsRemaining > 0

    val needsOrgSelection: Boolean
        get() = !needsOnboarding && orgs.size > 1 && activeOrgId == null

    val canWrite: Boolean
        get() = activeOrgId != null &&
            orgs.firstOrNull { it.id == activeOrgId }?.role in setOf("owner", "admin", "member") &&
            orgs.firstOrNull { it.id == activeOrgId }?.readOnly != true
}

class AuthController(
    private val authRepo: AuthRepository,
    private val orgRepo: OrgRepository,
    private val profileRepo: ProfileRepository,
    scope: CoroutineScope,
    private val emailCooldownMillis: Long = 60_000,
    private val nowMillis: () -> Long = {
        Clock.System.now().toEpochMilliseconds()
    },
    private val shouldCheckStaff: () -> Boolean = {true},
    private val requestedOrgId: () -> String? = { null },
    private val staffEntry: suspend () -> com.dotdrives.model.StaffEntry? = { null },
) : DotController<AuthUiState>(AuthUiState(), scope) {
    private var sessionJob: Job? = null
    private var hasHandledSession = false
    private var lastHandledSession: SessionInfo? = null
    private var loadGeneration = 0L
    private var cooldownJob: Job? = null

    fun start(
        recoveryTokenHash: String? = null,
        recoveryCallbackHint: Boolean = false,
        giverRequested: Boolean = false,
    ) {
        if (sessionJob?.isActive == true) return
        val generation = nextGeneration()
        update {
            it.copy(
                loading = true,
                error = null,
                bootstrapError = null,
                confirmationRequired = false,
            )
        }
        sessionJob = scope.launch {
            try {
                val sdkSnapshot = recoveryTokenHash
                    ?.let { tokenHash ->
                        AuthSessionSnapshot(
                            session = authRepo.verifyRecoveryToken(tokenHash).getOrThrow(),
                            source = AuthSessionSource.Recovery,
                        )
                    }
                    ?: authRepo.awaitInitialSession()
                val initialSnapshot = if (
                    recoveryTokenHash == null &&
                    recoveryCallbackHint &&
                    sdkSnapshot.source == AuthSessionSource.External &&
                    sdkSnapshot.session != null
                ) {
                    /*
                     * The default Supabase email template redirects through GoTrue's
                     * /verify endpoint, so External proves the SDK just accepted a
                     * server-established callback session. The free-tier template
                     * exposes `type` only as a client hint; relabeling another fresh
                     * callback can therefore expose password change only for that
                     * callback's already-authenticated account, not another user.
                     */
                    authRepo.grantRecovery(sdkSnapshot.session)
                } else {
                    sdkSnapshot
                }
                val initialSession = initialSnapshot.session
                if (isCurrent(generation)) {
                    update { current ->
                        if (isCurrent(generation)) {
                            current.copy(
                                recoveryPending = initialSession != null &&
                                    initialSnapshot.source == AuthSessionSource.Recovery,
                                giverContext = giverRequested,
                                audience = if (giverRequested) AuthAudience.Giver else current.audience,
                            )
                        } else {
                            current
                        }
                    }
                }
                val applyInitialSession = beginSession(
                    session = initialSession,
                    force = true,
                    generation = generation,
                )
                coroutineScope {
                    launch {
                        authRepo.session.collectLatest { session ->
                            if (hasHandledSession && session == lastHandledSession) {
                                return@collectLatest
                            }
                            handleSession(
                                session = session,
                                generation = nextGeneration(),
                            )
                        }
                    }
                    if (applyInitialSession) {
                        applySession(initialSession, generation)
                    }
                }
            } catch (error: Throwable) {
                if (error is CancellationException) throw error
                publishError(error, generation)
            }
        }
    }

    fun refresh() {
        val session = state.value.session ?: return
        val generation = nextGeneration()
        update { it.copy(loading = true, error = null, bootstrapError = null) }
        scope.launch {
            handleSession(
                session = session,
                force = true,
                generation = generation,
            )
        }
    }

    fun retryBootstrap() {
        refresh()
    }

    fun signIn(email: String, password: String) {
        val generation = nextGeneration()
        update {
            it.copy(
                loading = true,
                error = null,
                bootstrapError = null,
                confirmationRequired = false,
                credentialError = false,
            )
        }
        scope.launch {
            authRepo.signIn(email, password)
                .onSuccess { handleSession(it, force = true, generation = generation) }
                .onFailure { publishSignInError(it, generation) }
        }
    }

    fun signUp(email: String, password: String, redirectUrl: String? = null) {
        val generation = nextGeneration()
        update {
            it.copy(
                loading = true,
                error = null,
                bootstrapError = null,
                confirmationRequired = false,
                credentialError = false,
                formState = AuthFormState.SignUp,
            )
        }
        scope.launch {
            authRepo.signUp(email, password, redirectUrl)
                .onSuccess { outcome ->
                    when (outcome) {
                        is SignUpOutcome.SignedIn ->
                            handleSession(outcome.session, generation = generation)

                        SignUpOutcome.ConfirmationRequired -> {
                            if (!isCurrent(generation)) return@onSuccess
                            update { current ->
                                if (isCurrent(generation)) {
                                    AuthUiState(
                                        checked = true,
                                        confirmationRequired = true,
                                        formState = AuthFormState.ConfirmationSent,
                                        pendingEmail = email,
                                    )
                                } else {
                                    current
                                }
                            }
                        }
                    }
                }
                .onFailure { publishError(it, generation) }
        }
    }

    fun signInWithProvider(
        provider: AuthProvider,
        redirectUrl: String,
        giverContext: Boolean = false,
    ) {
        val generation = nextGeneration()
        update {
            it.copy(
                loading = true,
                error = null,
                magicLinkSent = false,
                passwordResetSent = false,
                passwordUpdated = false,
                audience = if (giverContext) AuthAudience.Giver else AuthAudience.Team,
                giverContext = giverContext,
                credentialError = false,
            )
        }
        scope.launch {
            authRepo.signInWithProvider(provider, redirectUrl)
                .onSuccess {
                    if (!isCurrent(generation)) return@onSuccess
                    update { current ->
                        if (isCurrent(generation)) current.copy(loading = false) else current
                    }
                }
                .onFailure { publishError(it, generation) }
        }
    }

    fun sendMagicLink(
        email: String,
        redirectUrl: String,
        giverContext: Boolean = false,
    ) {
        if (state.value.loading || state.value.emailCooldownActive) return
        val generation = nextGeneration()
        update {
            it.copy(
                loading = true,
                error = null,
                magicLinkSent = false,
                passwordResetSent = false,
                passwordUpdated = false,
                audience = if (giverContext) AuthAudience.Giver else AuthAudience.Team,
                giverContext = giverContext,
                credentialError = false,
            )
        }
        scope.launch {
            authRepo.sendMagicLink(email, redirectUrl)
                .onSuccess {
                    if (!isCurrent(generation)) return@onSuccess
                    val availableAt = nowMillis() + emailCooldownMillis
                    update { current ->
                        if (isCurrent(generation)) {
                            current.copy(
                                loading = false,
                                magicLinkSent = true,
                                formState = AuthFormState.MagicLinkSent,
                                pendingEmail = email,
                                resendAvailableAt = availableAt,
                                resendSecondsRemaining = cooldownSeconds(availableAt),
                            )
                        } else {
                            current
                        }
                    }
                    startEmailCooldown(availableAt)
                }
                .onFailure { publishError(it, generation) }
        }
    }

    fun sendPasswordReset(email: String, redirectUrl: String) {
        if (state.value.loading || state.value.emailCooldownActive) return
        val generation = nextGeneration()
        update {
            it.copy(
                loading = true,
                error = null,
                magicLinkSent = false,
                passwordResetSent = false,
                passwordUpdated = false,
                audience = AuthAudience.Team,
                giverContext = false,
                credentialError = false,
            )
        }
        scope.launch {
            authRepo.sendPasswordReset(email, redirectUrl)
                .onSuccess {
                    if (!isCurrent(generation)) return@onSuccess
                    val availableAt = nowMillis() + emailCooldownMillis
                    update { current ->
                        if (isCurrent(generation)) {
                            current.copy(
                                loading = false,
                                passwordResetSent = true,
                                formState = AuthFormState.PasswordResetSent,
                                pendingEmail = email,
                                resendAvailableAt = availableAt,
                                resendSecondsRemaining = cooldownSeconds(availableAt),
                            )
                        } else {
                            current
                        }
                    }
                    startEmailCooldown(availableAt)
                }
                .onFailure { publishError(it, generation) }
        }
    }

    fun updatePassword(newPassword: String) {
        val session = state.value.session
        if (
            !state.value.recoveryPending ||
            session == null ||
            !authRepo.hasValidRecoveryGrant(session.userId)
        ) {
            authRepo.clearRecoveryGrant()
            update {
                it.copy(
                    loading = false,
                    recoveryPending = false,
                    error = "This reset link is invalid or has expired.",
                )
            }
            return
        }
        val generation = nextGeneration()
        update {
            it.copy(
                loading = true,
                error = null,
                passwordUpdated = false,
                credentialError = false,
            )
        }
        scope.launch {
            authRepo.updatePassword(newPassword)
                .onSuccess {
                    if (!isCurrent(generation)) return@onSuccess
                    update { current ->
                        if (isCurrent(generation)) {
                            current.copy(
                                loading = false,
                                passwordUpdated = true,
                                recoveryPending = false,
                            )
                        } else {
                            current
                        }
                    }
                }
                .onFailure { publishError(it, generation) }
        }
    }

    fun clearAuthIntent() {
        update {
            it.copy(
                error = null,
                magicLinkSent = false,
                passwordResetSent = false,
                passwordUpdated = false,
                formState = AuthFormState.SignIn,
                credentialError = false,
            )
        }
    }

    fun selectAudience(audience: AuthAudience) {
        update {
            it.copy(
                audience = audience,
                giverContext = audience == AuthAudience.Giver,
                formState = AuthFormState.SignIn,
                error = null,
                credentialError = false,
                magicLinkSent = false,
                passwordResetSent = false,
                confirmationRequired = false,
            )
        }
    }

    fun showSignIn(preserveAudience: Boolean = false) {
        update {
            it.copy(
                audience = if (preserveAudience) it.audience else AuthAudience.Team,
                giverContext = preserveAudience && it.giverContext,
                formState = AuthFormState.SignIn,
                error = null,
                credentialError = false,
                magicLinkSent = false,
                passwordResetSent = false,
                confirmationRequired = false,
            )
        }
    }

    fun showSignUp() {
        update {
            it.copy(
                audience = AuthAudience.Team,
                giverContext = false,
                formState = AuthFormState.SignUp,
                error = null,
                credentialError = false,
                confirmationRequired = false,
            )
        }
    }

    fun showForgotPassword() {
        update {
            it.copy(
                audience = AuthAudience.Team,
                giverContext = false,
                formState = AuthFormState.ForgotPassword,
                error = null,
                credentialError = false,
            )
        }
    }

    fun clearError() {
        update { it.copy(error = null, credentialError = false) }
    }

    fun cancelRecovery() {
        authRepo.clearRecoveryGrant()
        update {
            it.copy(
                recoveryPending = false,
                error = null,
                credentialError = false,
            )
        }
    }

    private fun startEmailCooldown(availableAt: Long) {
        cooldownJob?.cancel()
        cooldownJob = scope.launch {
            var seconds = cooldownSeconds(availableAt)
            while (seconds > 0) {
                delay(1_000)
                seconds = minOf(seconds - 1, cooldownSeconds(availableAt))
                update { it.copy(resendSecondsRemaining = seconds) }
            }
        }
    }

    private fun cooldownSeconds(availableAt: Long): Int {
        val remaining = (availableAt - nowMillis()).coerceAtLeast(0)
        return ((remaining + 999) / 1_000).toInt()
    }

    /** Save changes only to the matching summary, retaining current organization selection. */
    fun updateFiscalPeriod(orgId: String, month: Int, day: Int) {
        update { current -> current.copy(orgs = current.orgs.map { org ->
            if (org.id == orgId) org.copy(fiscalStartMonth = month, fiscalStartDay = day) else org
        }) }
    }

    fun signOut() {
        val generation = nextGeneration()
        update {
            it.copy(
                loading = true,
                error = null,
                bootstrapError = null,
                confirmationRequired = false,
            )
        }
        scope.launch {
            authRepo.signOut()
                .onSuccess {
                    handleSession(
                        session = null,
                        force = true,
                        generation = generation,
                    )
                }
                .onFailure { publishError(it, generation) }
        }
    }

    fun selectOrg(orgId: String) {
        if (state.value.orgs.none { it.id == orgId }) return
        nextGeneration()
        update { current ->
            if (current.orgs.any { it.id == orgId }) {
                current.copy(
                    activeOrgId = orgId,
                    loading = false,
                    bootstrapError = null,
                )
            } else {
                current
            }
        }
    }

    fun clearOrgSelection() {
        update { current ->
            if (current.orgs.size > 1) {
                current.copy(activeOrgId = null)
            } else {
                current
            }
        }
    }

    private suspend fun handleSession(
        session: SessionInfo?,
        force: Boolean = false,
        generation: Long,
    ) {
        if (!beginSession(session, force, generation)) return
        applySession(session, generation)
    }

    private fun beginSession(
        session: SessionInfo?,
        force: Boolean,
        generation: Long,
    ): Boolean {
        if (!isCurrent(generation)) return false
        if (!force && hasHandledSession && session == lastHandledSession) return false
        if (state.value.session?.userId != session?.userId) {
            update { it.copy(profileDisplayName = null,staffRole=null,staffSignInRequired=false,staffEntryChecked=false) }
        }
        hasHandledSession = true
        lastHandledSession = session
        return true
    }

    private suspend fun applySession(session: SessionInfo?, generation: Long) {
        if (!isCurrent(generation)) return
        if (session == null) {
            update { current ->
                if (isCurrent(generation)) AuthUiState(checked = true, giverContext = current.giverContext, audience = current.audience) else current
            }
        } else {
            loadSession(session, generation)
        }
    }

    private suspend fun loadSession(session: SessionInfo, generation: Long) {
        // Resolve staff before customer bootstrap. A failed lookup must never create an
        // onboarding decision, and a late result belongs only to its session generation.
        val requestedOrg = requestedOrgId()
        update { if(isCurrent(generation)) it.copy(checked=false,staffRole=null,staffSignInRequired=false,staffEntryChecked=false,
            resolvedDeepLinkOrgId=null,deepLinkAttemptOrgId=requestedOrg) else it }
        if (requestedOrg != null) {
            orgRepo.openDeepLink(requestedOrg).getOrElse {
                publishBootstrapError(it, generation, session)
                return
            }
            if (!isCurrent(generation)) return
        }
        if(requestedOrg == null && !state.value.giverContext && !state.value.recoveryPending && shouldCheckStaff()) {
            val entry=try {staffEntry()} catch(e:Throwable) {publishBootstrapError(e,generation,session);return}
            if(!isCurrent(generation))return
            update {if(isCurrent(generation))it.copy(staffEntryChecked=true) else it}
            if(entry!=null) {
                update { current-> if(isCurrent(generation)) current.copy(
                    checked=true,session=session,staffRole=entry.role,staffSignInRequired=!entry.authorized,
                    profileDisplayName=null,orgs=emptyList(),activeOrgId=null,needsOnboarding=false,
                    loading=false,bootstrapError=null,confirmationRequired=false,
                    error=if(entry.authorized)null else "Sign in again to access Dot admin. Super admins can use a password or magic link; Admin staff use Google.",
                ) else current }
                return
            }
        }
        val orgs = orgRepo.myOrgs().getOrElse {
            publishBootstrapError(it, generation, session)
            return
        }
        if (!isCurrent(generation)) return
        if (requestedOrg != null && orgs.none { it.id == requestedOrg }) {
            publishBootstrapError(IllegalStateException("You do not have access to this organization."), generation, session)
            return
        }
        val profile = profileRepo.me().getOrElse {
            publishBootstrapError(it, generation, session)
            return
        }
        if (!isCurrent(generation)) return
        val currentProfile = profile?.takeIf { it.userId == session.userId }
        val displayName = currentProfile?.let { listOf(it.firstName.trim(), it.lastName.trim()).filter(String::isNotBlank).joinToString(" ").ifBlank { null } }
        val retainedOrgId = state.value
            .takeIf { it.session?.userId == session.userId }
            ?.activeOrgId
            ?.takeIf { selectedId -> orgs.any { it.id == selectedId } }
        update { current ->
            if (isCurrent(generation)) {
                current.copy(
                    checked = true,
                    session = session,
                    profileDisplayName = displayName,
                    orgs = orgs,
                    activeOrgId = requestedOrg ?: retainedOrgId ?: orgs.singleOrNull()?.id,
                    resolvedDeepLinkOrgId = requestedOrg,
                    giverContext = if (requestedOrg != null) false else current.giverContext,
                    audience = if (requestedOrg != null) AuthAudience.Team else current.audience,
                    needsOnboarding = orgs.isEmpty(),
                    loading = false,
                    error = null,
                    bootstrapError = null,
                    confirmationRequired = false,
                )
            } else {
                current
            }
        }
    }

    private fun Profile?.hasName(): Boolean =
        this != null && (firstName.isNotBlank() || lastName.isNotBlank())

    private fun publishBootstrapError(
        error: Throwable,
        generation: Long,
        session: SessionInfo,
    ) {
        if (error is CancellationException) throw error
        if (!isCurrent(generation)) return
        update { current ->
            if (isCurrent(generation)) {
                current.copy(
                    checked = true,
                    session = session,
                    profileDisplayName = null,
                    orgs = emptyList(),
                    activeOrgId = null,
                    needsOnboarding = false,
                    loading = false,
                    error = null,
                    bootstrapError = error.message ?: "Unable to load your account",
                )
            } else {
                current
            }
        }
    }

    private fun publishError(
        error: Throwable,
        generation: Long,
        session: SessionInfo? = state.value.session,
    ) {
        if (error is CancellationException) {
            throw error
        }
        if (!isCurrent(generation)) return
        update { current ->
            if (isCurrent(generation)) {
                current.copy(
                    checked = true,
                    session = session,
                    profileDisplayName = current.profileDisplayName.takeIf { current.session?.userId == session?.userId },
                    loading = false,
                    error = error.message ?: "Something went wrong",
                )
            } else {
                current
            }
        }
    }

    private fun publishSignInError(error: Throwable, generation: Long) {
        if (error is CancellationException) throw error
        if (!isCurrent(generation)) return
        val message = error.message.orEmpty()
        val structuredCode = (error as? AuthRestException)
            ?.let { it.errorCode?.value ?: it.error }
            ?.takeIf(String::isNotBlank)
        val isCredentialError = isCredentialAuthError(structuredCode)
        if (!isCredentialError) {
            publishError(error, generation)
            return
        }
        update { current ->
            if (isCurrent(generation)) {
                current.copy(
                    checked = true,
                    loading = false,
                    error = "That email and password don't match. Try again, use a magic link, or reset your password.",
                    credentialError = true,
                )
            } else {
                current
            }
        }
    }

    private fun nextGeneration(): Long {
        loadGeneration += 1
        return loadGeneration
    }

    private fun isCurrent(generation: Long): Boolean =
        generation == loadGeneration
}

/**
 * Supabase credential failures currently use `invalid_credentials`; `invalid_grant`
 * is retained for compatible GoTrue deployments. Unstructured and unknown failures
 * keep their original message.
 */
internal fun isCredentialAuthError(errorCode: String?): Boolean =
    errorCode?.lowercase() in setOf("invalid_credentials", "invalid_grant")
